import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/NumericInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { TypeImageMarkingSection, newLiteId } from '@/components/TypeImageMarkingSection';

export const getLiteErrors = (lite) => {
  const errors = {};
  const w = parseFloat(lite.width_in);
  const h = parseFloat(lite.height_in);
  if (lite.width_in === '' || lite.width_in === null || lite.width_in === undefined || isNaN(w)) errors.width_in = 'This field is required';
  if (lite.height_in === '' || lite.height_in === null || lite.height_in === undefined || isNaN(h)) errors.height_in = 'This field is required';
  if (!lite.liteType) errors.liteType = 'This field is required';
  if (lite.liteType === 'glass' && !lite.glassTypeId) errors.glassTypeId = 'This field is required';
  if (lite.liteType === 'metalPanel' && !lite.metalPanelTypeId) errors.metalPanelTypeId = 'This field is required';
  return errors;
};

export const hasLiteErrors = (lites = []) =>
  lites.some(l => Object.keys(getLiteErrors(l)).length > 0);

const GRID = 'grid grid-cols-[100px_100px_100px_120px_1fr_40px] gap-2';

// Shared schedule details section: dimensions + lites + Type image marking.
// Used by the Window Schedule (step 2) and the Door Schedule (step 2 / edit modal).
// Fully controlled: `value` holds the staged detail fields, `onChange` receives the next value.
export const ScheduleDetailsStep = ({
  value,
  onChange,
  glassTypes = [],
  metalPanelTypes = [],
  touched = {},
  setTouched,
  dimensionsTitle = 'Dimensions',
  dimensionFields = [],
  litesHint = 'Add glass or metal panel lites',
  type = null,
  typeImageUrl = null,
  markingTitle = 'Type Image Marking',
  typeLabel = 'Type',
  markTestIdPrefix = 'type-mark',
  liteTestIdPrefix = 'lite',
  showUnmarkedWarning = false,
}) => {
  const lites = value.lites || [];
  const marks = value.liteMarks || [];

  const markTouched = (index, field) =>
    setTouched(prev => ({ ...prev, [index]: { ...(prev[index] || {}), [field]: true } }));

  const handleAddLite = () => {
    onChange({
      ...value,
      lites: [...lites, { id: newLiteId(), liteType: 'glass', width_in: '', height_in: '', glassTypeId: '', metalPanelTypeId: '' }]
    });
  };

  const handleRemoveLite = (index) => {
    const removed = lites[index];
    const markCount = marks.filter(m => m.liteId === removed?.id).length;
    if (markCount > 0 && !window.confirm(
      `Lite ${index + 1} has ${markCount} mark${markCount === 1 ? '' : 's'} on the ${typeLabel} image. Deleting the lite discards ${markCount === 1 ? 'it' : 'them'}. Continue?`
    )) {
      return;
    }
    onChange({
      ...value,
      lites: lites.filter((_, i) => i !== index),
      liteMarks: marks.filter(m => m.liteId !== removed?.id)
    });
    setTouched(prev => {
      const next = {};
      Object.keys(prev).forEach(key => {
        const i = Number(key);
        if (i < index) next[i] = prev[key];
        else if (i > index) next[i - 1] = prev[key];
      });
      return next;
    });
  };

  const handleLiteChange = (index, field, fieldValue) => {
    markTouched(index, field);
    const updated = lites.map((lite, i) => {
      if (i !== index) return lite;
      if (field === 'liteType') {
        return {
          ...lite,
          liteType: fieldValue,
          glassTypeId: fieldValue === 'glass' ? lite.glassTypeId : '',
          metalPanelTypeId: fieldValue === 'metalPanel' ? lite.metalPanelTypeId : ''
        };
      }
      return { ...lite, [field]: fieldValue };
    });
    onChange({ ...value, lites: updated });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-lg border-b pb-2">{dimensionsTitle}</h3>
        <div className="grid grid-cols-2 gap-4">
          {dimensionFields.map(field => (
            <div className="space-y-2" key={field.key}>
              <Label>{field.label}</Label>
              <NumericInput
                positive
                allowFraction
                value={value[field.key] || ''}
                onChange={(v) => onChange({ ...value, [field.key]: v })}
                placeholder={field.placeholder}
                data-testid={field.testId}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <div>
            <h3 className="font-semibold text-lg">Lites</h3>
            <p className="text-xs text-slate-500 mt-1">{litesHint}</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleAddLite} className="flex items-center gap-1" data-testid={`add-${liteTestIdPrefix}-btn`}>
            <Plus className="h-4 w-4" /> Add Lite
          </Button>
        </div>

        {lites.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <div className={`${GRID} bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 uppercase`}>
              <div></div><div>Width (in) *</div><div>Height (in) *</div><div>Lite Type *</div><div>Glass / Metal Panel *</div><div></div>
            </div>
            <div className="divide-y divide-slate-200">
              {lites.map((lite, index) => {
                const errors = getLiteErrors(lite);
                const t = touched[index] || {};
                const liteMarkCount = marks.filter(m => m.liteId === lite.id).length;
                const unmarked = showUnmarkedWarning && !!typeImageUrl && liteMarkCount === 0;
                return (
                  <div key={lite.id || index} className="bg-white hover:bg-slate-50">
                    <div className={`${GRID} px-3 py-2 items-start`}>
                      <div className="text-sm font-medium text-slate-700 pt-2">Lite {index + 1}</div>
                      <div>
                        <NumericInput positive allowFraction value={lite.width_in ?? ''} onChange={(v) => handleLiteChange(index, 'width_in', v)} placeholder="0" className={`h-8 text-sm ${t.width_in && errors.width_in ? 'border-red-500' : ''}`} data-testid={`${liteTestIdPrefix}-${index}-width`} />
                        {t.width_in && errors.width_in && <p className="text-[10px] text-red-500 mt-0.5">{errors.width_in}</p>}
                      </div>
                      <div>
                        <NumericInput positive allowFraction value={lite.height_in ?? ''} onChange={(v) => handleLiteChange(index, 'height_in', v)} placeholder="0" className={`h-8 text-sm ${t.height_in && errors.height_in ? 'border-red-500' : ''}`} data-testid={`${liteTestIdPrefix}-${index}-height`} />
                        {t.height_in && errors.height_in && <p className="text-[10px] text-red-500 mt-0.5">{errors.height_in}</p>}
                      </div>
                      <div>
                        <Select value={lite.liteType || 'glass'} onValueChange={(val) => handleLiteChange(index, 'liteType', val)}>
                          <SelectTrigger className={`h-8 text-sm ${t.liteType && errors.liteType ? 'border-red-500' : ''}`} data-testid={`${liteTestIdPrefix}-${index}-type`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="glass">Glass</SelectItem><SelectItem value="metalPanel">Metal Panel</SelectItem></SelectContent>
                        </Select>
                        {t.liteType && errors.liteType && <p className="text-[10px] text-red-500 mt-0.5">{errors.liteType}</p>}
                      </div>
                      <div>
                        {lite.liteType === 'metalPanel' ? (
                          <>
                            <Select value={lite.metalPanelTypeId || 'none'} onValueChange={(val) => handleLiteChange(index, 'metalPanelTypeId', val === 'none' ? '' : val)}>
                              <SelectTrigger className={`h-8 text-sm ${t.metalPanelTypeId && errors.metalPanelTypeId ? 'border-red-500' : ''}`} data-testid={`${liteTestIdPrefix}-${index}-metal-panel`}><SelectValue placeholder="Select Metal Panel" /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{metalPanelTypes.map(mp => <SelectItem key={mp.id} value={mp.id}>{mp.name}</SelectItem>)}</SelectContent>
                            </Select>
                            {t.metalPanelTypeId && errors.metalPanelTypeId && <p className="text-[10px] text-red-500 mt-0.5">{errors.metalPanelTypeId}</p>}
                          </>
                        ) : (
                          <>
                            <Select value={lite.glassTypeId || 'none'} onValueChange={(val) => handleLiteChange(index, 'glassTypeId', val === 'none' ? '' : val)}>
                              <SelectTrigger className={`h-8 text-sm ${t.glassTypeId && errors.glassTypeId ? 'border-red-500' : ''}`} data-testid={`${liteTestIdPrefix}-${index}-glass-type`}><SelectValue placeholder="Select Glass Type" /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{glassTypes.map(gt => <SelectItem key={gt.id} value={gt.id}>{gt.name}</SelectItem>)}</SelectContent>
                            </Select>
                            {t.glassTypeId && errors.glassTypeId && <p className="text-[10px] text-red-500 mt-0.5">{errors.glassTypeId}</p>}
                          </>
                        )}
                      </div>
                      <div className="flex justify-center pt-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveLite(index)} className="h-8 w-8 p-0 rounded-md text-red-500 hover:bg-red-600 hover:text-white dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white transition-colors" data-testid={`${liteTestIdPrefix}-${index}-remove`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {unmarked && (
                      <div className="flex items-center gap-1.5 px-3 pb-2 text-[11px] text-amber-700" data-testid={`${liteTestIdPrefix}-${index}-unmarked-warning`}>
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        This Lite has not been marked on the {typeLabel} image.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg bg-slate-50">
            <p className="text-slate-500 text-sm">No lites added yet</p>
            <Button type="button" variant="outline" size="sm" onClick={handleAddLite} className="mt-3" data-testid={`add-first-${liteTestIdPrefix}-btn`}><Plus className="h-4 w-4 mr-1" /> Add First Lite</Button>
          </div>
        )}
      </div>

      <TypeImageMarkingSection
        type={type}
        imageUrl={typeImageUrl}
        lites={lites}
        marks={marks}
        onChangeMarks={(nextMarks) => onChange({ ...value, liteMarks: nextMarks })}
        title={markingTitle}
        typeLabel={typeLabel}
        testIdPrefix={markTestIdPrefix}
      />
    </div>
  );
};

export default ScheduleDetailsStep;
